/** Sign-in (mockup v7/v9): endorsed DocVault logo, Microsoft first, email + password. */
import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { LOCAL_AUTH, cognitoMessage, confirmNewPassword, forgotPassword } from "../auth/cognito";
import { cognitoBase, startMicrosoftSignIn } from "../auth/microsoft";
import { LoginArt } from "../auth/LoginArt";

const PRIVACY_URL = import.meta.env.VITE_PRIVACY_URL as string | undefined;
const TERMS_URL = import.meta.env.VITE_TERMS_URL as string | undefined;

type Mode = "signin" | "newpw" | "forgot" | "reset" | "mfa";

export function LoginPage() {
  const { signIn, completeNewPassword, submitMfa } = useAuth();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  // Microsoft sign-in is offered once the stack has a Microsoft tenant configured
  // (the public /health endpoint says so); until then the button explains.
  const [msOn, setMsOn] = useState(false);
  useEffect(() => {
    if (LOCAL_AUTH || !cognitoBase()) return;
    fetch(`${import.meta.env.VITE_API_URL}/health`).then((r) => r.json())
      .then((h: { signin?: { microsoft?: boolean } }) => setMsOn(!!h.signin?.microsoft), () => setMsOn(false));
  }, []);
  const microsoft = () => {
    setErr(""); setInfo("");
    if (!msOn) return setInfo("Microsoft sign-in isn't switched on yet. Use your email and password for now.");
    if (!email.trim()) return setErr("Enter your work email first, then choose Sign in with Microsoft.");
    setBusy(true);
    startMicrosoftSignIn(email).catch((e) => { setBusy(false); setErr((e as Error).message); });
  };

  const run = async (fn: () => Promise<void>) => {
    setErr(""); setInfo(""); setBusy(true);
    try { await fn(); } catch (e) {
      if ((e as { code?: string }).code === "NEW_PASSWORD_REQUIRED") { setMode("newpw"); setInfo("Choose a new password to finish signing in."); }
      else if ((e as { code?: string }).code === "MFA_CODE_REQUIRED") { setMode("mfa"); setCode(""); }
      else setErr(LOCAL_AUTH ? (e as Error).message : cognitoMessage(e));
    } finally { setBusy(false); }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "signin") {
      if (!LOCAL_AUTH && !email.trim()) return setErr("Enter your work email.");
      if (!LOCAL_AUTH && !pw) return setErr("Enter your password.");
      void run(() => signIn(email, pw));
    } else if (mode === "newpw") {
      if (pw2.length < 12) return setErr("Use at least 12 characters.");
      void run(() => completeNewPassword(email, pw, pw2));
    } else if (mode === "mfa") {
      if (!/^\d{6}$/.test(code.replace(/\s+/g, ""))) return setErr("Enter the 6-digit code from your authenticator app.");
      void run(() => submitMfa(code));
    } else if (mode === "forgot") {
      if (!email.trim()) return setErr("Enter your work email.");
      void run(async () => { await forgotPassword(email.trim()); setMode("reset"); setInfo("If that email has an account, a reset code is on its way."); });
    } else {
      if (!code.trim() || pw2.length < 12) return setErr("Enter the code and a new password of at least 12 characters.");
      void run(async () => { await confirmNewPassword(email.trim(), code.trim(), pw2); setMode("signin"); setPw(""); setPw2(""); setCode(""); setInfo("Password changed. Sign in with your new password."); });
    }
  };

  // With Microsoft on, the email comes first: it picks the person's company tenant.
  const msButton = (
    <button className="btn btn-ms" type="button" onClick={microsoft} disabled={busy}>
      <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true"><rect x="1" y="1" width="9" height="9" fill="#F25022" /><rect x="11" y="1" width="9" height="9" fill="#7FBA00" /><rect x="1" y="11" width="9" height="9" fill="#00A4EF" /><rect x="11" y="11" width="9" height="9" fill="#FFB900" /></svg>
      Sign in with Microsoft
    </button>
  );
  const emailField = (
    <label><span className="lbl">Work email</span>
      <input className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" autoFocus disabled={mode === "reset"} />
    </label>
  );
  return (
    <div className="login">
      <LoginArt />
      <div className="form-side">
        <form onSubmit={submit} noValidate>
          <h1>{mode === "signin" ? "Sign in" : mode === "newpw" ? "Set a new password" : mode === "mfa" ? "Enter your code" : "Reset your password"}</h1>
          {mode === "mfa" && <>
            <p className="help" style={{ marginTop: -4 }}>Signed in as <b>{email}</b>. Open your authenticator app and enter the 6-digit code for DocVault.</p>
            <label><span className="lbl">6-digit code</span>
              <input className="inp mono" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" inputMode="numeric" autoFocus maxLength={7}
                style={{ fontSize: 22, letterSpacing: ".3em", textAlign: "center" }} />
            </label>
          </>}
          {mode === "signin" && !msOn && <>{msButton}<div className="or"><span>or use your email</span></div></>}
          {mode !== "newpw" && mode !== "mfa" && emailField}
          {mode === "signin" && msOn && <>{msButton}<div className="or"><span>or use a password</span></div></>}
          {mode === "signin" && (
            <label>
              <span className="lbl" style={{ display: "flex", justifyContent: "space-between" }}>
                Password
                {!LOCAL_AUTH && <button type="button" className="linkbtn tiny" onClick={() => { setMode("forgot"); setErr(""); setInfo(""); }}>Forgot password?</button>}
              </span>
              <input className="inp" type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password"
                placeholder={LOCAL_AUTH ? "Not needed in local development" : ""} />
            </label>
          )}
          {mode === "reset" && (
            <label><span className="lbl">Reset code from the email</span>
              <input className="inp" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" inputMode="numeric" />
            </label>
          )}
          {(mode === "newpw" || mode === "reset") && (
            <label><span className="lbl">New password</span>
              <input className="inp" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" />
              <span className="help">At least 12 characters, with upper and lower case, a number and a symbol.</span>
            </label>
          )}
          <div className="err" role="alert">{err}</div>
          {info && <div className="ok-msg">{info}</div>}
          <button className={`btn btn-accent btn-block ${busy ? "busy" : ""}`} type="submit" disabled={busy}>
            {busy ? "Please wait…" : mode === "signin" ? "Sign in" : mode === "mfa" ? "Verify and sign in" : mode === "forgot" ? "Email me a reset code" : "Save new password"}
          </button>
          {mode !== "signin" && (
            <button type="button" className="linkbtn" onClick={() => { setMode("signin"); setErr(""); setInfo(""); }}>Back to sign in</button>
          )}
          {mode === "mfa" && <p className="access">Lost or replaced your phone? Ask an admin in your company to reset your two-step sign-in, then set it up again.</p>}
          {mode !== "mfa" && <p className="access">{msOn ? "Staff of the Confluence Enterprises companies can sign in with Microsoft; an admin then gives you access to your company's documents." : "Access is by invitation through your organization or ITAssurance."} Need access? <a href="mailto:support@itassurancemsp.com">support@itassurancemsp.com</a></p>}
          {(PRIVACY_URL || TERMS_URL) && (
            <div className="legal">
              {PRIVACY_URL && <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy</a>}
              {PRIVACY_URL && TERMS_URL && <span>·</span>}
              {TERMS_URL && <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms</a>}
            </div>
          )}
          {LOCAL_AUTH && <div className="help" style={{ borderTop: "1px dashed var(--line)", paddingTop: 10 }}>Local development: the email you enter decides who you are. Leave it blank to be the default admin (LOCAL_USER_EMAIL).</div>}
        </form>
      </div>
    </div>
  );
}
